/** Audit contract shared with the TOPS Offline API (dbo.tblOfflineAuditLog). */

export type AuditLogSource = 'ONLINE' | 'OFFLINE' | 'APP_LEVEL';

export type AuditEventCategory =
  | 'AUTHENTICATION'
  | 'AUTHORIZATION'
  | 'DATA_ACCESS'
  | 'DATA_CHANGE'
  | 'PHOTO'
  | 'SYNC'
  | 'VALIDATION'
  | 'NOTE'
  | 'CONFLICT'
  | 'SECURITY'
  | 'SYSTEM';

export type AuditSeverity = 'INFO' | 'WARNING' | 'ERROR' | 'CRITICAL';

export type AuditOutcome = 'SUCCESS' | 'FAILURE';

export type AuthorizationDecision = 'ALLOW' | 'DENY' | 'NOT_APPLICABLE';

/**
 * Note the absence of a user id: the server derives identity from the bearer token, so a device
 * cannot attribute its actions to someone else.
 */
export interface AuditEventRecord {
  eventId: string;
  transactionId: string;
  correlationId?: string;
  logSource: AuditLogSource;
  category: AuditEventCategory;
  action: string;
  severity: AuditSeverity;
  outcome: AuditOutcome;
  authorizationDecision?: AuthorizationDecision;
  deviceId: string;
  deviceInfo?: string;
  entityType?: string;
  jobNumber?: string;
  httpMethod?: string;
  requestPath?: string;
  httpStatus?: number;
  clientEventTimeUtc: string;
  message?: string;
  detail?: unknown;
}

/** Everything a caller supplies; the service fills in ids, device and timing. */
export type AuditEventInput = Omit<
  AuditEventRecord,
  'eventId' | 'transactionId' | 'logSource' | 'deviceId' | 'clientEventTimeUtc' | 'severity' | 'outcome'
> &
  Partial<Pick<AuditEventRecord, 'eventId' | 'transactionId' | 'logSource' | 'severity' | 'outcome'>>;

export interface AuditIngestResponse {
  received: number;
  accepted: number;
  duplicate: number;
  rejected: number;
  rejectedEventIds: string[];
}

export const AuditAction = {
  LOGIN_SUCCEEDED: 'LOGIN_SUCCEEDED',
  LOGIN_FAILED: 'LOGIN_FAILED',
  LOGOUT: 'LOGOUT',
  OFFLINE_SESSION_RESTORED: 'OFFLINE_SESSION_RESTORED',
  OFFLINE_SESSION_EXPIRED: 'OFFLINE_SESSION_EXPIRED',
  AUTHORIZATION_DENIED: 'AUTHORIZATION_DENIED',
  HTTP_GET: 'HTTP_GET',
  JOB_LIST_VIEWED: 'JOB_LIST_VIEWED',
  JOB_DOWNLOADED_FOR_OFFLINE: 'JOB_DOWNLOADED_FOR_OFFLINE',
  JOB_SAVED: 'JOB_SAVED',
  MULTI_DWELLING_SAVED: 'MULTI_DWELLING_SAVED',
  VALIDATION_COMPLETED: 'VALIDATION_COMPLETED',
  VALIDATION_FAILED: 'VALIDATION_FAILED',
  NOTE_CREATED: 'NOTE_CREATED',
  PHOTO_CAPTURED: 'PHOTO_CAPTURED',
  PHOTO_PRESIGN_REQUESTED: 'PHOTO_PRESIGN_REQUESTED',
  PHOTO_UPLOADED: 'PHOTO_UPLOADED',
  CONFLICT_DETECTED: 'CONFLICT_DETECTED',
  CONFLICT_RESOLVED: 'CONFLICT_RESOLVED',
  SYNC_DATA: 'SYNC_DATA',
  SYNC_PHOTO: 'SYNC_PHOTO',
  SYNC_DATA_AND_PHOTO: 'SYNC_DATA_AND_PHOTO',
  SYNC_FAILED: 'SYNC_FAILED',
  SYNC_BLOCKED_TAMPER_DETECTED: 'SYNC_BLOCKED_TAMPER_DETECTED',
} as const;

export type AuditActionName = (typeof AuditAction)[keyof typeof AuditAction];
